import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormCheck } from 'react-bootstrap';
import { isNull } from 'lodash';

import useAcl from '@hooks/useAcl';

import { VIEW_LISTING_CLICKED } from '@redux/constants/ActionTypes';
import { loadListingToCopyById, loadMyListings, toggleShowBrokerListings } from '@redux/modules/listing';
import { loadCityNames, loadFormDefinition } from '@redux/modules/formGeneration';
import { selectMyListings } from '@redux/selectors/ListingSelector';
import { getUserName } from '@redux/selectors/UserSelector';

import ListingsTableWidget from '@components/widget/ListingsTableWidget';
import CreateListingWidget from '@components/widget/CreateListingWidget';
import Panel from '@components/smartAdminUI/Panel';
import Button from '@components/smartAdminUI/Button';
import ListingsDropdown from '@components/dropdowns/ListingsDropdown';
import ProspectsWidget from '@components/widget/ProspectsWidget';
import CreaWebformsWidget from '@components/widget/CreaWebformsWidget';
import CloudCMAWidget from '@components/widget/CloudCMAWidget';
import NotificationsCarousel from '@components/notifications/NotificationsCarousel';

import GAEventTracking from '@components/tracking/GAEventTracking';

// Landing page
const Dashboard = () => {
  const lookupCities = { lookupnames: ['Lookup_Property_Listing_City'] }; // hardcoded since cities lookup string is not changing
  const [displayResults, setDisplayResults] = useState([]);
  const [isBroker, setIsBroker] = useState(false);
  const { aclString } = useAcl();
  const { mainActions } = useAcl();

  const dispatch = useDispatch();
  const history = useHistory();
  const cityNames = useSelector(state => state.formGeneration.cityNames);
  const allListings = useSelector(selectMyListings);
  const showBrokerListings = useSelector(state => state.listing.showBrokerListings);
  const username = useSelector(getUserName);

  const gaEventTracker = GAEventTracking('User');

  const viewListing = displayListing => dispatch({
    type: VIEW_LISTING_CLICKED, data: displayListing,
  });

  useEffect(() => {
    if (username !== null) {
      gaEventTracker('Entry Dashboard', `${username}`);
    }
  });

  useEffect(() => {
    setIsBroker(aclString.includes('BROKER'));
  });

  useEffect(() => {
    dispatch(loadCityNames(lookupCities));
  }, []);

  // retrieves city lookup table values on mount
  useEffect(() => {
    dispatch(loadMyListings());
  }, []);

  /*
    listener for search listings results and city name redux states
    finds city name that matches city code in lookup tables
    if the city item is active, city codes are replaced with their description (actual city name)
    update city values directly in allListings and then set displayResults
   */
  useEffect(() => {
    if (isNull(allListings)) return;
    (allListings ?? []).map((listing, index) => {
      // default to null
      let cityString = null;
      // translate city ID to description when available
      if (listing?.City && cityNames) {
        const cityItem = cityNames[listing.City];
        if (cityItem?.Active) {
          cityString = cityItem.Description;
        }
      }
      allListings[index].City = cityString;
      return null;
    });
    setDisplayResults(allListings);
  }, [allListings, cityNames]);

  // Build where clause with UniqueID to retrieve records for
  const buildWhereClause = uniqueID => {
    const whereClause = {
      where: {
        clause: [
          {
            name: 'UniqueID',
            value: uniqueID,
          },
        ],
      },
    };
    return whereClause;
  };

  const listingSelected = row => {
    viewListing(row);
  };

  const copyClicked = row => {
    dispatch(loadFormDefinition('create', 'listings', row?.PropertyType?.toLowerCase(), null, 'listing', true));
    const copyRequest = buildWhereClause(row.UniqueID);
    copyRequest.formName = row?.PropertyType?.toLowerCase();
    dispatch(loadListingToCopyById(copyRequest));
  };

  const auditClicked = row => {
    history.push(`/audit?ListingID=${row.ListingID}`);
  };

  return (
    <div>
      <div className="subheader">
        <h1 className="subheader-title">
          <i className="subheader-icon fal fa-window mr-2" />
          Dashboard
        </h1>
      </div>
      <NotificationsCarousel />
      <div className="row">
        {isBroker ? (
          <>
            <div className="col-xl-7 d-flex">
              <Panel
                header="My Listings"
                customClass="w-100"
                footer={
                  isBroker ? (
                    <FormCheck
                      type="switch"
                      onChange={() => dispatch(toggleShowBrokerListings(!showBrokerListings))}
                      label="Brokerage Listings"
                      checked={showBrokerListings}
                      id="brokerListingSwitch"
                      className="ml-3"
                    />
                  ) : null
                }
              >
                <ListingsTableWidget
                  displayData={displayResults}
                  onListingSelect={listingSelected}
                  onCopyClick={copyClicked}
                  onAuditClick={auditClicked}
                />
              </Panel>
            </div>
            <div className="col-xl-5 d-flex">
              <Panel
                header="Create Listing"
                customClass="w-100"
                panelToolbar={(
                  <>
                    <div className="btn-group">
                      {mainActions.ListingsCreate && (
                        <Button
                          variant="primary dropdown-toggle"
                          data-toggle="dropdown"
                          aria-haspopup="true"
                          aria-expanded="false"
                        >
                          <i className="fas fa-plus mr-2" />
                          Create Listing
                        </Button>
                      )}
                      <ListingsDropdown />
                    </div>
                  </>
                )}
              >
                <CreateListingWidget />
              </Panel>
            </div>
          </>
        ) : (
          <>
            <div className="col-xl-6 d-flex">
              <Panel
                header="My Listings"
                customClass="w-100"
                footer={
                  aclString.includes('BROKER') ? (
                    <FormCheck
                      type="switch"
                      onChange={() => dispatch(toggleShowBrokerListings(!showBrokerListings))}
                      label="Brokerage Listings"
                      checked={showBrokerListings}
                      id="brokerListingSwitch"
                      className="ml-3"
                    />
                  ) : null
                }
              >
                <ListingsTableWidget
                  displayData={displayResults}
                  onListingSelect={listingSelected}
                  onCopyClick={copyClicked}
                  onAuditClick={auditClicked}
                />
              </Panel>
            </div>
            <div className="col-xl-6 d-flex">
              <Panel
                header="Create Listing"
                customClass="w-100"
                panelToolbar={(
                  <>
                    <div className="btn-group">
                      {mainActions.ListingsCreate && (
                        <Button
                          variant="primary dropdown-toggle"
                          data-toggle="dropdown"
                          aria-haspopup="true"
                          aria-expanded="false"
                        >
                          <i className="fas fa-plus mr-2" />
                          Create Listing
                        </Button>
                      )}
                      <ListingsDropdown />
                    </div>
                  </>
                )}
              >
                <CreateListingWidget />
              </Panel>
            </div>
          </>
        )}
        <div className="col-xl-4 d-flex">
          <Panel customClass="w-100">
            <CreaWebformsWidget />
          </Panel>
        </div>
        <div className="col-xl-4 d-flex">
          <Panel customClass="w-100">
            <ProspectsWidget />
          </Panel>
        </div>
        <div className="col-xl-4 d-flex">
          <Panel customClass="w-100">
            <CloudCMAWidget />
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
